import { joiResolver } from "@hookform/resolvers/joi";
import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { useForm as useFormController, useWatch } from "react-hook-form";

import {
    Gender,
    calculateAgeGroup,
    formatGender,
    generateFullName,
    participantValidationSchemas,
    validationOptions,
} from "@bujus/common";
import { useApiServiceContext, useDialogDisplayer } from "@bujus/common-frontend";

import { Collapse, DialogClosingButton, Form, MaskedInput, RadioSelect, TextField } from "@/base";
import { useSelectedEventStateContext } from "@/contexts";
import { useSnackbarsController } from "@/hooks";
import { birthYearMask } from "@/utilities";

import { GroupSelect } from "../../common";
import { GroupCreationDialog } from "../../common/group-creation-dialog";

const ParticipantUpdateDialog = ({ close, participant, isOpen, removalDialogDisplayer }) => {
    const snackbarsController = useSnackbarsController();

    const queryClient = useQueryClient();

    const apiService = useApiServiceContext();
    const [selectedEvent] = useSelectedEventStateContext();

    const [latestAgeGroup, setLatestAgeGroup] = useState();

    const groupCreationDialogDisplayer = useDialogDisplayer(GroupCreationDialog);

    const formController = useFormController({
        mode: "onChange",
        resolver: joiResolver(participantValidationSchemas.update.form, validationOptions),
        context: { event: selectedEvent },
        defaultValues: {
            schoolInternalId:
                participant.schoolInternalId === undefined ? "" : participant.schoolInternalId,
            name: {
                first: participant.name.first,
                last: participant.name.last,
            },
            gender: participant.gender,
            birthYear: participant.birthYear.toString(),
            class: participant.class === undefined ? "" : participant.class,
            group: participant.group === undefined ? null : participant.group,
        },
    });
    const birthYearFieldValue = useWatch({
        control: formController.control,
        name: "birthYear",
    });

    const mutation = useMutation(
        async (variables) =>
            apiService.participants.updateAsync(variables.id, variables.participant),
        {
            onError: (error) => {
                snackbarsController.enqueueError(error);
            },
            onSuccess: (updatedParticipant) => {
                snackbarsController.enqueueSuccessMessage(
                    `${generateFullName(
                        updatedParticipant.name.first,
                        updatedParticipant.name.last,
                    )} geändert`,
                );
                queryClient.invalidateQueries(["participants"]);
                close();
            },
        },
    );

    const tryClose = () => {
        if (mutation.isLoading) {
            return false;
        }
        close();
        return true;
    };

    const handleClose = () => {
        tryClose();
    };

    const handleFormSubmit = (data) => {
        if (mutation.isLoading) {
            return;
        }
        mutation.mutate({
            id: participant.id,
            participant: {
                ...(data.schoolInternalId !== "" && { schoolInternalId: data.schoolInternalId }),
                name: {
                    first: data.name.first,
                    last: data.name.last,
                },
                gender: data.gender,
                birthYear: Number(data.birthYear),
                ...(data.class !== "" && { class: data.class }),
                ...(data.group !== null && { groupId: data.group.id }),
            },
        });
    };

    const handleClosingButtonClick = () => {
        tryClose();
    };

    const handleRemovalButtonClick = () => {
        if (!tryClose()) {
            return;
        }
        removalDialogDisplayer.open({ participant });
    };

    useEffect(() => {
        // HACK Joi does not throw an error, when the value was full 4 characters and then the user deletes one character
        if (
            birthYearFieldValue.replace("_", "").length !== 4 ||
            formController.formState.errors.birthYear !== undefined
        ) {
            return;
        }
        setLatestAgeGroup(calculateAgeGroup(selectedEvent.start, Number(birthYearFieldValue)));
    }, [formController.formState.errors.birthYear, birthYearFieldValue]);

    return (
        <>
            <Dialog onClose={handleClose} open={isOpen}>
                <Form controller={formController} onSubmit={handleFormSubmit}>
                    <DialogTitle>Teilnehmer bearbeiten</DialogTitle>
                    <DialogClosingButton
                        isDisabled={mutation.isLoading}
                        onClick={handleClosingButtonClick}
                    />
                    <DialogContent>
                        <Box display="flex" flexDirection="column" gap={2}>
                            <TextField
                                fieldName="schoolInternalId"
                                isAutofocusing
                                isEnabled={!mutation.isLoading}
                                label="Schulinterne-ID"
                            />
                            <TextField
                                fieldName="name.first"
                                isEnabled={!mutation.isLoading}
                                label="Vorname *"
                            />
                            <TextField
                                fieldName="name.last"
                                isEnabled={!mutation.isLoading}
                                label="Nachname *"
                            />
                            <RadioSelect
                                fieldName="gender"
                                formatItem={formatGender}
                                isEnabled={!mutation.isLoading}
                                items={Object.values(Gender)}
                                label="Geschlecht *"
                            />
                            <MaskedInput
                                fieldName="birthYear"
                                isEnabled={!mutation.isLoading}
                                label="Geburtsjahr *"
                                mask={birthYearMask}
                            />
                            <Collapse
                                isOpen={
                                    birthYearFieldValue.replace("_", "").length === 4 &&
                                    formController.formState.errors.birthYear === undefined
                                }
                                overwritingTopMargin={2}
                            >
                                <Typography>
                                    <b>Altersgruppe:</b> {latestAgeGroup}
                                </Typography>
                            </Collapse>
                            <TextField
                                fieldName="class"
                                isEnabled={!mutation.isLoading}
                                label="Klasse"
                            />
                            <GroupSelect
                                creationDialogDisplayer={groupCreationDialogDisplayer}
                                fieldName="group"
                                isEnabled={!mutation.isLoading}
                                label="Riege"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            color="secondary"
                            disabled={mutation.isLoading}
                            onClick={handleRemovalButtonClick}
                            startIcon={<DeleteIcon />}
                        >
                            Entfernen
                        </Button>
                        <LoadingButton
                            loading={mutation.isLoading}
                            startIcon={<EditIcon />}
                            type="submit"
                        >
                            Ändern
                        </LoadingButton>
                    </DialogActions>
                </Form>
            </Dialog>
            {groupCreationDialogDisplayer.element}
        </>
    );
};

export { ParticipantUpdateDialog };
